import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { categoryAPI } from '../services/api';

// Query keys for cache management
export const categoryKeys = {
    all: ['categories'],
    lists: () => [...categoryKeys.all, 'list'],
    details: () => [...categoryKeys.all, 'detail'],
    detail: (id) => [...categoryKeys.details(), id],
};

// Fetch all categories
export const useCategories = () => {
    return useQuery({
        queryKey: categoryKeys.lists(),
        queryFn: async () => {
            const response = await categoryAPI.getAll();
            return response.data;
        },
    });
};

// Fetch single category by ID
export const useCategory = (id) => {
    return useQuery({
        queryKey: categoryKeys.detail(id),
        queryFn: async () => {
            const response = await categoryAPI.getById(id);
            return response.data;
        },
        enabled: !!id,
    });
};

// Create category mutation
export const useCreateCategory = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data) => categoryAPI.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: categoryKeys.lists() });
        },
    });
};

// Update category mutation
export const useUpdateCategory = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }) => categoryAPI.update(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: categoryKeys.detail(variables.id) });
            queryClient.invalidateQueries({ queryKey: categoryKeys.lists() });
        },
    });
};

// Delete category mutation
export const useDeleteCategory = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id) => categoryAPI.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: categoryKeys.lists() });
        },
    });
};

// Toggle category status mutation
export const useToggleCategoryStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id) => categoryAPI.toggleStatus(id),
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: categoryKeys.detail(id) });
            queryClient.invalidateQueries({ queryKey: categoryKeys.lists() });
        },
    });
};
